import React, { FunctionComponent, ReactElement, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { TryOnContext } from '../../hooks/tryonContext';
import {
	DefaultBanubaFineTuningSettings,
	DefaultDeepARFineTuningSettings,
	DefaultMazingFineTuningSettings,
	TryOnFineTuningBanubaSettings,
	TryOnFineTuningDeepARSettings,
	TryOnFineTuningMazingSettings,
	TryOnGenericFineTuningSettings,
	TryOnProduct,
	TryOnProvider,
	TryOnProvidersTypesMapping,
	TryOnSeller,
	TryOnType
} from '../../models';
import { useInternalState } from '../../state/provider';
import { TryOnData, TryOnMode, ZakekeTryOnExposedMethods } from './tryOnModels';
import { BanubaTryOn } from './tryOnProviders/tryOnBanuba';
import { DeepARTryOn } from './tryOnProviders/tryOnDeepAR';
import { MazingPupillaryDistance, MazingTryOn } from './tryOnProviders/tryOnMazing';
import { IS_ANDROID, IS_IOS } from '@zakeke/zakeke-3d-core';

const TryOnOverlay = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	background-color: black;
	opacity: 0.5;
	left: 0;
	top: 0;
	z-index: 3;
`;

const TryOnViewContainer = styled.div`
	width: 100%;
	height: 100%;
	position: fixed;
	z-index: 6;
	left: 0%;
	top: 0%;
`;

const PupillaryDistanceViewContainer = styled.div`
	width: 100%;
	height: 100%;
	position: fixed;
	background-color: white;
	z-index: 6;
	left: 0%;
	top: 0%;
`;

export interface ZakekeTryOnViewerProps {
	children?: React.ReactNode;
	className?: string;
	switchable?: boolean;
	onPDUpdated: (pd: number) => void;
	onReady?: () => void;
	onLoaded?: () => void;
	onClose: () => void;
	onWebcamError: () => void;
}

export const ZakekeTryOnViewer = forwardRef<ZakekeTryOnExposedMethods, ZakekeTryOnViewerProps>((props, ref) => {
	console.log('props ZakekeTryonviewer', props);
	const internalState = useInternalState();
	const stateRef = useRef(internalState);
	stateRef.current = internalState;
	const store = stateRef.current?.state.environment;

	const [tryon, setTryon] = useState<ZakekeTryOnExposedMethods>({});
	const [tryOnPickedMode, setTryOnPickedMode] = useState<TryOnMode>(TryOnMode.TryOn);
	const [tryOnVisibility, setTryOnVisibility] = useState(false);
	const frameRef = { tryon, setTryon, tryOnPickedMode, setTryOnPickedMode, tryOnVisibility, setTryOnVisibility };
	const [dataFetched, setDataFetched] = useState({} as TryOnData);
	const [switchToPDTool, setSwitchToPDTool] = useState(false);
	const [hasDataBeenFetched, setHasDataBeenFetched] = useState(false);

	const handleFrameReady = (sellerID: number, provider: number) => {
		// conteggio view per billing
		store.tryOnCountView(sellerID, provider, tryOnPickedMode);
		props.onReady?.();
	};

	useEffect(() => {
		setTryon({
			...tryon,
			mode: tryOnPickedMode,
			changeMode: (mode: TryOnMode) => {
				setTryOnPickedMode(mode);
			},
			visible: tryOnVisibility,
			setVisible: (visible: boolean) => {
				setTryOnVisibility(visible);
			}
		});
	}, []);

	useEffect(() => {
		setSwitchToPDTool(!!props.switchable);
	}, [props.switchable]);

	useMemo(() => {
		setTryon({
			...tryon,
			mode: tryOnPickedMode
		});
	}, [tryOnPickedMode]);

	useEffect(() => {
		if (tryOnVisibility) {
			if (hasDataBeenFetched && !switchToPDTool) setHasDataBeenFetched(false);
			console.log('fetching data');
			store.exportTryOnMeshToGlb().then((blob) => {
				if (store.tryOnSellerSettings) {
					const product = store.tryOnProductSettings!;
					const customer = store.tryOnSellerSettings;
					setDataFetched({
						product: product,
						customer: customer,
						provider: product.providerID,
						// forzo al momento a 3
						// provider: 3,
						mode: tryOnPickedMode,
						obj: blob,
						settings: store.tryOnMeshVisibleSettings
					});
					setTimeout(() => {
						setHasDataBeenFetched(true);
					}, 500);
				}
			});
		}
	}, [tryOnPickedMode, tryOnVisibility]);
	// Espongo i metodi esterni come reference per controllare il tryon/pd tool
	useImperativeHandle(
		ref,
		() => ({
			closeFrame: tryon.closeFrame,
			takeScreenshot: tryon.takeScreenshot,
			switchToPDTool: tryon.switchToPDTool,
			switchToTryOn: tryon.switchToTryOn,
			startPupillaryDistance: tryon.startPupillaryDistance,
			mode: tryon.mode,
			changeMode: tryon.changeMode,
			visible: tryon.visible,
			setVisible: tryon.setVisible
		}),
		[tryon]
	);

	return (
		<TryOnContext.Provider value={frameRef}>
			{tryOnVisibility && (
				<>
					{hasDataBeenFetched && tryOnPickedMode === TryOnMode.PDTool && !switchToPDTool && (
						<PupillaryDistanceView
							className={'tryon-pd-tool' + (props.className ? ' ' + props.className : '')}
							provider={dataFetched.provider!}
							product={dataFetched.product!}
							customer={dataFetched.customer!}
							onReady={() => handleFrameReady(dataFetched.customer!.sellerID, dataFetched.product!.providerID)}
							onLoaded={props.onLoaded}
							onPDUpdated={props.onPDUpdated}
							onClose={props.onClose}
							onWebcamError={props.onWebcamError}
						/>
					)}
					{hasDataBeenFetched && (tryOnPickedMode === TryOnMode.TryOn || switchToPDTool) && (
						<TryOnView
							className={'tryon-view-tool' + (props.className ? ' ' + props.className : '')}
							provider={dataFetched.product!.providerID}
							type={dataFetched.product!.typeID}
							obj={dataFetched.obj!}
							settings={store.tryOnMeshVisibleSettings}
							product={dataFetched.product!}
							customer={dataFetched.customer!}
							onPDUpdated={props.onPDUpdated}
							onReady={() => handleFrameReady(dataFetched.customer!.sellerID, dataFetched.product!.providerID)}
							onLoaded={props.onLoaded}
							onClose={props.onClose}
							onWebcamError={props.onWebcamError}
						/>
					)}
					{props.children}
					<TryOnOverlay />
				</>
			)}
		</TryOnContext.Provider>
	);
});
export interface TryOnViewProps {
	className?: string;
	provider: TryOnProvider;
	type: TryOnType;
	obj: Blob;
	settings?: TryOnGenericFineTuningSettings;
	product: TryOnProduct;
	customer: TryOnSeller;
	onReady?: () => void;
	onLoaded?: () => void;
	onClose: () => void;
	onPDUpdated?: (pd: number) => void;
	onWebcamError: () => void;
}
export const TryOnView: FunctionComponent<TryOnViewProps> = (props): ReactElement<any, any> | null => {
	console.log(JSON.stringify(props, null, 2) + ' tryonview');
	const internalState = useInternalState();
	const stateRef = useRef(internalState);
	stateRef.current = internalState;
	const store = stateRef.current?.state.environment;

	const pickViewer = () => {
		const mappings: TryOnProvidersTypesMapping = store.tryOnProvidersSettings ?? { list: {} };
		let currentProvider = TryOnProvider.NotSelected;
		console.log('currentProvider', currentProvider);
		if (mappings && mappings.list) {
			if (props.type.toString() in mappings.list) {
				currentProvider = mappings.list[props.type.toString()];
			}
		}
		switch (currentProvider) {
			case TryOnProvider.Mazing:
				return (
					<MazingTryOn
						obj={props.obj}
						className={props.className}
						product={props.product}
						customer={props.customer}
						fineTuningSettings={props.settings ? (props.settings as TryOnFineTuningMazingSettings) : DefaultMazingFineTuningSettings}
						onReady={props.onReady}
						onLoaded={props.onLoaded}
						onClose={props.onClose}
						onPDUpdated={props.onPDUpdated}
						onWebcamError={props.onWebcamError}
					/>
				);
			case TryOnProvider.Banuba:
				return (
					<BanubaTryOn
						obj={props.obj}
						className={props.className}
						product={props.product}
						customer={props.customer}
						fineTuningSettings={props.settings ? (props.settings as TryOnFineTuningBanubaSettings) : DefaultBanubaFineTuningSettings}
						onReady={props.onReady}
						onLoaded={props.onLoaded}
						onClose={props.onClose}
						onPDUpdated={props.onPDUpdated}
						onWebcamError={props.onWebcamError}
					/>
				);
			case TryOnProvider.DeepAR:
				return (
					<DeepARTryOn
						obj={props.obj}
						className={props.className}
						product={props.product}
						customer={props.customer}
						fineTuningSettings={props.settings ? (props.settings as TryOnFineTuningDeepARSettings) : DefaultDeepARFineTuningSettings}
						onReady={props.onReady}
						onLoaded={props.onLoaded}
						onClose={props.onClose}
						onPDUpdated={props.onPDUpdated}
						onWebcamError={props.onWebcamError}
					/>
				);
			default:
				return <div>Unsupported Type</div>;
		}
	};

	return (props && props.provider === TryOnProvider.DeepAR && !(IS_ANDROID || IS_IOS) ? (
		<></>
	) : (
		<TryOnViewContainer className={props.className ? props.className : '' + ' tryon-viewer'}>{pickViewer()}</TryOnViewContainer>
	));
};

export interface PupillaryDistanceViewProps {
	className?: string;
	provider: TryOnProvider;
	product: TryOnProduct;
	customer: TryOnSeller;
	onReady?: () => void;
	onLoaded?: () => void;
	onPDUpdated?: (pd: number) => void;
	onClose: () => void;
	onWebcamError: () => void;
}

export const PupillaryDistanceView: FunctionComponent<PupillaryDistanceViewProps> = (props) => {
	const pickViewer = () => {
		switch (props.provider) {
			case TryOnProvider.Mazing:
				return (
					<MazingPupillaryDistance
						product={props.product}
						customer={props.customer}
						onPDUpdated={props.onPDUpdated}
						onReady={props.onReady}
						onLoaded={props.onLoaded}
						className={props.className}
						onClose={props.onClose}
						onWebcamError={props.onWebcamError}
					/>
				);
			default:
				return <div>Unsupported Provider</div>;
		}
	};

	return <PupillaryDistanceViewContainer className={props.className}>{pickViewer()}</PupillaryDistanceViewContainer>;
};
